import { Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import logoWhiteWebp from "@assets/Ben Boaz Transport Logo - w_1764522353654.webp";
import logoWhitePng from "@assets/Ben Boaz Transport Logo - w_1764522353654.png";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-foreground text-background py-16">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <picture>
              <source srcSet={logoWhiteWebp} type="image/webp" />
              <img
                src={logoWhitePng}
                alt="Ben Boaz Transport LLC"
                width={168}
                height={56}
                className="h-14 w-auto mb-4"
              />
            </picture>
            <p className="text-background/80 mb-4 leading-relaxed">
              Reliable regional flatbed transportation for steel, lumber, machinery, and building materials.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/BenBoazTransport"
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 w-10 rounded-full bg-background/10 hover:bg-background/20 flex items-center justify-center transition-colors"
                aria-label="Facebook"
                data-testid="link-facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links - Services */}
          <div>
            <h4 className="font-bold text-lg mb-4">Services</h4>
            <ul className="space-y-3 text-background/80">
              <li>
                <Link
                  href="/services"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-flatbed"
                >
                  Flatbed Trucking
                </Link>
              </li>
              <li>
                <Link
                  href="/services"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-steel"
                >
                  Steel Hauling
                </Link>
              </li>
              <li>
                <Link
                  href="/services"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-lumber"
                >
                  Lumber Transport
                </Link>
              </li>
              <li>
                <Link
                  href="/services"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-machinery"
                >
                  Machinery Hauling
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links - Company */}
          <div>
            <h4 className="font-bold text-lg mb-4">Company</h4>
            <ul className="space-y-3 text-background/80">
              <li>
                <Link
                  href="/about"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-about"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  href="/careers"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-careers-footer"
                >
                  Driver Careers
                </Link>
              </li>
              <li>
                <Link
                  href="/fleet"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-fleet-footer"
                >
                  Our Fleet
                </Link>
              </li>
              <li>
                <Link
                  href="/blog"
                  className="text-background/80 hover:text-background px-2 py-1 font-normal inline-block transition-colors"
                  data-testid="footer-link-blog"
                >
                  Blog & Resources
                </Link>
              </li>
              <li>
                <Button
                  variant="ghost"
                  asChild
                  className="text-background/80 hover:text-background h-auto px-2 py-1 font-normal justify-start"
                  data-testid="footer-link-admin"
                >
                  <a
                    href="/admin"
                    rel="nofollow"
                  >
                    Admin Portal
                  </a>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  asChild
                  className="text-background/80 hover:text-background h-auto px-2 py-1 font-normal justify-start"
                  data-testid="footer-link-welcome"
                >
                  <Link href="/welcome">
                    Project Delivery
                  </Link>
                </Button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3 text-background/80">
              <li>
                <div className="text-sm font-semibold mb-1">Office</div>
                <a
                  href="tel:8327325501"
                  className="hover:text-background transition-colors hover-elevate px-2 py-1 rounded-md inline-block"
                  data-testid="footer-link-phone-office"
                >
                  (832) 732-5501
                </a>
              </li>
              <li>
                <div className="text-sm font-semibold mb-1">Dispatch</div>
                <a
                  href="tel:8327973242"
                  className="hover:text-background transition-colors hover-elevate px-2 py-1 rounded-md inline-block"
                  data-testid="footer-link-phone-dispatch"
                >
                  (832) 797-3242
                </a>
              </li>
              <li>
                <div className="text-sm font-semibold mb-1">Emergencies</div>
                <a
                  href="tel:2819434708"
                  className="hover:text-background transition-colors hover-elevate px-2 py-1 rounded-md inline-block"
                  data-testid="footer-link-phone-emergency"
                >
                  (281) 943-4708
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@benboaztransport.com"
                  className="hover:text-background transition-colors hover-elevate px-2 py-1 rounded-md inline-block"
                  data-testid="footer-link-email"
                >
                  info@benboaztransport.com
                </a>
              </li>
              <li className="pt-4">
                <Button
                  variant="secondary"
                  className="w-full font-bold"
                  asChild
                  data-testid="footer-button-contact"
                >
                  <Link href="/contact">
                    GET IN TOUCH
                  </Link>
                </Button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-background/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-background/70">
            <div>
              © {currentYear} Ben Boaz Transport LLC. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center md:justify-end gap-4 md:gap-6">
              <Link 
                href="/accessibility" 
                className="hover:text-background transition-colors"
                data-testid="footer-link-accessibility"
              >
                Accessibility
              </Link>
              <Link 
                href="/terms" 
                className="hover:text-background transition-colors"
                data-testid="footer-link-terms"
              >
                Terms & Conditions
              </Link>
              <Link 
                href="/privacy" 
                className="hover:text-background transition-colors"
                data-testid="footer-link-privacy"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
          <div className="flex justify-center md:justify-end gap-6 mt-4 text-sm text-background/70">
            <span className="hover:text-background transition-colors">DOT Certified</span>
            <span className="hover:text-background transition-colors">Fully Insured</span>
            <span className="hover:text-background transition-colors">Licensed Carrier</span>
          </div>
          <div className="flex justify-center mt-6 text-sm text-background/60">
            <span>
              Website designed by{" "}
              <a
                href="https://integradyn.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-background/80 hover:text-background transition-colors underline underline-offset-2"
                data-testid="link-integradyn"
              >
                Integradyn.ai
              </a>
              {" "}- We help websites rank!
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
